import { ReceivedOrderProduct, SkuWeightKind } from "../utils/scanner-backend";

export interface StoredIntakeEntry {
  QuantityReceived: number;
  BatchNumber: string;
}

export interface StoredProduct {
  StockItemId: number;
  IntakeEntries: StoredIntakeEntry[];
  WeightG: number | null;
  WeightKind: SkuWeightKind;
}

type WeightSnapshot = Pick<ReceivedOrderProduct, "StockItemId" | "WeightG" | "WeightKind">;
type WeightComparison = {
  WeightG: number | null;
  WeightKind?: SkuWeightKind | null;
};

function getWeightKind(weightKind: SkuWeightKind | null | undefined) {
  return weightKind ?? SkuWeightKind.Estimated;
}

function getBaselineProduct(
  stockItemId: number,
  baselineProducts: WeightSnapshot[],
) {
  return baselineProducts.find((product) => product.StockItemId === stockItemId);
}

export function hasWeightChange(
  product: WeightComparison,
  baselineProduct?: WeightComparison,
) {
  return product.WeightG !== (baselineProduct?.WeightG ?? null)
    || getWeightKind(product.WeightKind) !== getWeightKind(baselineProduct?.WeightKind);
}

export function getPersistedSessionProducts(
  receivedItems: ReceivedOrderProduct[],
  baselineProducts: WeightSnapshot[],
): StoredProduct[] {
  return receivedItems.reduce<StoredProduct[]>((persistedProducts, product) => {
    const baselineProduct = getBaselineProduct(product.StockItemId, baselineProducts);
    const shouldPersist = product.IntakeEntries.length > 0 || hasWeightChange({
      WeightG: product.WeightG,
      WeightKind: getWeightKind(product.WeightKind),
    }, baselineProduct);

    if (!shouldPersist) {
      return persistedProducts;
    }

    persistedProducts.push({
      StockItemId: product.StockItemId,
      IntakeEntries: product.IntakeEntries.map((entry) => ({
        QuantityReceived: entry.QuantityReceived,
        BatchNumber: entry.BatchNumber,
      })),
      WeightG: product.WeightG,
      WeightKind: getWeightKind(product.WeightKind),
    });

    return persistedProducts;
  }, []);
}

export function sessionHasRestorableChanges(
  sessionProducts: StoredProduct[],
  baselineProducts: WeightSnapshot[],
) {
  return sessionProducts.some((product) => {
    if (product.IntakeEntries.length > 0) {
      return true;
    }

    const baselineProduct = getBaselineProduct(product.StockItemId, baselineProducts);
    return hasWeightChange(product, baselineProduct);
  });
}
